import { ClassCodeFlightEnum } from '@msc-b2c/interfaces/enums';
import { IFlightClassConfig } from '@msc-b2c/interfaces/interfaces';

const FLIGHT_CLASS_CODE_MAP: Record<string, ClassCodeFlightEnum> = {
  Y: ClassCodeFlightEnum.ECONOMY,
  W: ClassCodeFlightEnum.PREMIUM_CLASS,
  C: ClassCodeFlightEnum.BUSINESS_CLASS,
  F: ClassCodeFlightEnum.FIRST_CLASS,
};

export const DEFAULT_FLIGHT_CLASSES: IFlightClassConfig[] = [
  { code: 'Y', label: 'Economy' },
  { code: 'W', label: 'Economy Premium' },
  { code: 'C', label: 'Business' },
  { code: 'F', label: 'First Class' },
];

export type ConfiguredFlightClass = {
  label: string;
  classCode: ClassCodeFlightEnum;
};

export const mapFlightClassCodeToEnum = (code: string): ClassCodeFlightEnum | undefined =>
  FLIGHT_CLASS_CODE_MAP[code.toUpperCase()];

export const getConfiguredFlightClasses = (
  flightClasses: IFlightClassConfig[]
): ConfiguredFlightClass[] => {
  const source = flightClasses.length > 0 ? flightClasses : DEFAULT_FLIGHT_CLASSES;

  return source
    .map(({ label, code }) => {
      const classCode = mapFlightClassCodeToEnum(code);
      return classCode ? { label, classCode } : null;
    })
    .filter((entry): entry is ConfiguredFlightClass => entry !== null);
};

export const getFlightClassLabel = (
  classCode: ClassCodeFlightEnum | undefined,
  flightClasses: IFlightClassConfig[] = []
): string => {
  if (!classCode) return '';

  return (
    getConfiguredFlightClasses(flightClasses).find(({ classCode: code }) => code === classCode)
      ?.label ?? ''
  );
};

export const formatFlightAirlineWithClass = ({
  airlineName,
  airlineCode,
  flightNumber,
  classCode,
  flightClasses = [],
}: {
  airlineName: string;
  airlineCode?: string;
  flightNumber?: string;
  classCode?: ClassCodeFlightEnum;
  flightClasses?: IFlightClassConfig[];
}): string => {
  const airlinePart = `${airlineName}${
    flightNumber && airlineCode
      ? ` (${airlineCode}-${flightNumber})`
      : airlineCode
      ? ` (${airlineCode})`
      : ''
  }`;
  const classLabel = getFlightClassLabel(classCode, flightClasses);

  return classLabel ? `${airlinePart} - ${classLabel}` : airlinePart;
};

/**
 * Resolves the travel class pre-selected when the change-flight modal opens.
 * Priority: AEM component preselected code (Y/W/C/F) → current booking class → first CA-config class.
 */
export const resolvePreselectedClassCode = (
  availableClasses: ConfiguredFlightClass[],
  options: {
    componentPreselectedCode?: string;
    currentFlightClassCode?: ClassCodeFlightEnum;
  }
): ClassCodeFlightEnum => {
  const availableCodes = new Set(availableClasses.map(({ classCode }) => classCode));
  const firstAvailable = availableClasses[0]?.classCode ?? ClassCodeFlightEnum.ECONOMY;

  if (options.componentPreselectedCode) {
    const mapped = mapFlightClassCodeToEnum(options.componentPreselectedCode);
    if (mapped && availableCodes.has(mapped)) {
      return mapped;
    }
  }

  if (options.currentFlightClassCode && availableCodes.has(options.currentFlightClassCode)) {
    return options.currentFlightClassCode;
  }

  return firstAvailable;
};

export const isUpgradedFlightClass = (classCode: ClassCodeFlightEnum): boolean =>
  classCode !== ClassCodeFlightEnum.ECONOMY;
